/**
 * mask-edge.js — put a mask's edge ON the picture's edge, by colour.
 *
 * A mask from a segmenter's grid (EdgeTAM answers on 1024², trackers on 480)
 * lands a few pixels off the object's real edge at the working size. In the
 * EDGE BAND — where a window holds both mask and non-mask pixels — every pixel
 * is re-decided: it belongs to whichever side's LOCAL mean colour it is closer
 * to. Inside and far outside nothing changes; where the two sides share a
 * colour (a black piano on a black curtain, a black sofa on a dark floor) the
 * mask stays as it was — no colour rule separates them.
 *
 * Two owners, one function:
 *   · /video-bg-remover «Сохранить предмет» (keep.js) — an orange ring of stage
 *     floor round a piano lid at 1280 px (23.09.2026);
 *   · object paint (object-paint/op-object.js) — a red rim round a dress turned
 *     emerald, and the opposite: a colour-blind band painted a dark floor next
 *     to a black sofa cream (21.10.2026). Moved here from keep.js unchanged.
 * A guided filter was tried first and left 0.56 on a pixel 2 px past a hard
 * edge (tests/js/video-bg-remover-keep.test.mjs): it smooths toward the
 * frame's edges, it does not move the mask onto them.
 *
 * UMD: window.MaskEdge / self.MaskEdge (workers) / module.exports.
 *   refineEdge(core, rgba, w, h, r) → Float32Array(w*h) 0..1 | null (empty mask)
 *   boxMean(src, w, h, r)          → Float32Array — mean over a (2r+1)² window
 */
(function (root, factory) {
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    root.MaskEdge = api;
}(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : globalThis), function () {
    'use strict';

    /* Box mean over a (2r+1)² window via an integral image, on a w×h plane. */
    function boxMean(src, w, h, r) {
        var W1 = w + 1, I = new Float64Array(W1 * (h + 1));
        for (var y = 0; y < h; y++) {
            var row = 0;
            for (var x = 0; x < w; x++) { row += src[y * w + x]; I[(y + 1) * W1 + x + 1] = I[y * W1 + x + 1] + row; }
        }
        var out = new Float32Array(w * h);
        for (var yy = 0; yy < h; yy++) {
            var y0 = Math.max(0, yy - r), y1 = Math.min(h, yy + r + 1);
            for (var xx = 0; xx < w; xx++) {
                var x0 = Math.max(0, xx - r), x1 = Math.min(w, xx + r + 1);
                var sum = I[y1 * W1 + x1] - I[y0 * W1 + x1] - I[y1 * W1 + x0] + I[y0 * W1 + x0];
                out[yy * w + xx] = sum / ((y1 - y0) * (x1 - x0));
            }
        }
        return out;
    }

    /* The silhouette comes from a ~480-px grid, so at the working size its edge
       is off by a few pixels either way — measured as an orange ring of stage
       floor round a piano lid. In the EDGE BAND (where the window holds both
       mask and non-mask pixels) every pixel is re-decided by colour: it belongs
       to whichever side's local mean colour it is closer to. Inside and far
       outside nothing changes. A guided filter was tried first and left 0.56 on
       a pixel 2 px past a hard edge (tests/js/video-bg-remover-keep.test.mjs):
       it smooths towards the frame's edges, it does not move the mask onto them.
       Black on black (a piano against a curtain) stays whatever the mask said —
       no colour rule separates it. */
    function refineEdge(core, frameData, w, h, r) {
        var x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
            if (!core[y * w + x]) continue;
            if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
        if (x1 < 0) return null;
        var pad = 2 * r + 2;
        x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
        x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
        var rw = x1 - x0 + 1, rh = y1 - y0 + 1, n = rw * rh;
        var P = new Float32Array(n), R = new Float32Array(n), G = new Float32Array(n), Bc = new Float32Array(n);
        var RP = new Float32Array(n), GP = new Float32Array(n), BP = new Float32Array(n);
        for (var j = 0; j < rh; j++) for (var i = 0; i < rw; i++) {
            var k = (y0 + j) * w + (x0 + i), o = k * 4, q = j * rw + i, pv = core[k] ? 1 : 0;
            P[q] = pv; R[q] = frameData[o]; G[q] = frameData[o + 1]; Bc[q] = frameData[o + 2];
            RP[q] = R[q] * pv; GP[q] = G[q] * pv; BP[q] = Bc[q] * pv;
        }
        // means over the window: of the mask, of all colour, of the colour under the mask
        var mP = boxMean(P, rw, rh, r), mR = boxMean(R, rw, rh, r), mG = boxMean(G, rw, rh, r), mB = boxMean(Bc, rw, rh, r);
        var mRP = boxMean(RP, rw, rh, r), mGP = boxMean(GP, rw, rh, r), mBP = boxMean(BP, rw, rh, r);
        var out = new Float32Array(w * h);
        for (var jj = 0; jj < rh; jj++) for (var ii = 0; ii < rw; ii++) {
            var qq = jj * rw + ii, fp = mP[qq], k2 = (y0 + jj) * w + (x0 + ii);
            if (fp <= 0.001 || fp >= 0.999) { out[k2] = P[qq]; continue; }     // not the edge band
            var inR = mRP[qq] / fp, inG = mGP[qq] / fp, inB = mBP[qq] / fp;
            var fo = 1 - fp;
            var outR = (mR[qq] - mRP[qq]) / fo, outG = (mG[qq] - mGP[qq]) / fo, outB = (mB[qq] - mBP[qq]) / fo;
            var dI = (R[qq] - inR) * (R[qq] - inR) + (G[qq] - inG) * (G[qq] - inG) + (Bc[qq] - inB) * (Bc[qq] - inB);
            var dO = (R[qq] - outR) * (R[qq] - outR) + (G[qq] - outG) * (G[qq] - outG) + (Bc[qq] - outB) * (Bc[qq] - outB);
            var sep = (inR - outR) * (inR - outR) + (inG - outG) * (inG - outG) + (inB - outB) * (inB - outB);
            // two sides the same colour: colour cannot decide, keep the mask
            if (sep < 400) { out[k2] = P[qq]; continue; }
            out[k2] = dO / (dI + dO + 1e-6);
        }
        return out;
    }

    return { boxMean: boxMean, refineEdge: refineEdge };
}));
